/**
 * Webhooks admin page.
 *
 * Surfaces the webhook CRUD already exposed by `routes/webhooks.ts` —
 * admins can register URLs to receive platform events (orders, contacts,
 * newsletter, chatbot events, etc.), send test deliveries, and inspect
 * the last 50 delivery attempts. The HMAC secret is shown ONCE on
 * creation and must be copied immediately.
 *
 * Opt-in by construction: the list is empty by default; no events are
 * sent anywhere until an admin deliberately registers a URL.
 */
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-provider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Webhook,
  Plus,
  Trash2,
  Play,
  Copy,
  CheckCircle2,
  XCircle,
  Loader2,
  Power,
} from 'lucide-react';

// Kept in sync with aapastech-backend/src/lib/plugin-framework/event-bus.ts.
// Edit this list when new PlatformEventType members ship.
const EVENT_CATALOG: Array<{ group: string; events: string[] }> = [
  {
    group: 'Contact & Newsletter',
    events: [
      'contact.submitted',
      'newsletter.subscribed',
      'newsletter.unsubscribed',
      'newsletter.confirmed',
    ],
  },
  {
    group: 'E-commerce',
    events: [
      'order.created',
      'order.paid',
      'order.shipped',
      'order.delivered',
      'order.cancelled',
      'order.refunded',
      'cart.updated',
      'cart.abandoned',
    ],
  },
  {
    group: 'CRM',
    events: [
      'crm.contact.created',
      'crm.contact.updated',
      'crm.deal.created',
      'crm.deal.stage_changed',
    ],
  },
  {
    group: 'Billing',
    events: [
      'subscription.created',
      'subscription.cancelled',
      'subscription.renewed',
      'payment.received',
      'payment.failed',
    ],
  },
  { group: 'Website', events: ['website.deployed', 'website.updated'] },
  {
    group: 'Chatbot',
    events: [
      'chatbot.conversation.started',
      'chatbot.message.sent',
      'chatbot.handoff.requested',
      'chatbot.lead.captured',
      'chatbot.feedback.received',
    ],
  },
  {
    group: 'Plugin Lifecycle',
    events: ['plugin.installed', 'plugin.uninstalled', 'plugin.config_changed'],
  },
];

interface WebhookRow {
  id: string;
  name: string;
  url: string;
  events: string[];
  isActive: boolean;
  secretHint?: string;
  createdAt: string;
  updatedAt?: string;
}

interface DeliveryRow {
  _id: string;
  webhookId: string;
  eventType: string;
  url: string;
  statusCode?: number;
  success: boolean;
  error?: string;
  attemptCount: number;
  deliveredAt: string;
}

export default function WebhooksPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const confirm = useConfirm();

  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);

  const webhooks = useQuery({
    queryKey: ['webhooks', projectId, 'list'],
    queryFn: () =>
      api
        .get<{ success: boolean; data: WebhookRow[] }>(`/webhooks/${projectId}`)
        .then((r) => r.data.data),
    enabled: Boolean(projectId),
  });

  const logs = useQuery({
    queryKey: ['webhooks', projectId, 'logs'],
    queryFn: () =>
      api
        .get<{ success: boolean; data: DeliveryRow[] }>(`/webhooks/${projectId}/logs`)
        .then((r) => r.data.data),
    refetchInterval: 15_000,
    enabled: Boolean(projectId),
  });

  const create = useMutation({
    mutationFn: (payload: { name: string; url: string; events: string[] }) =>
      api
        .post<{ success: boolean; data: { secret: string } }>(`/webhooks/${projectId}`, payload)
        .then((r) => r.data.data),
    onSuccess: (d) => {
      setNewSecret(d.secret);
      setAddOpen(false);
      qc.invalidateQueries({ queryKey: ['webhooks', projectId] });
      toast.success('Webhook created. Copy the signing secret now.');
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message || 'Failed to create webhook'),
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/webhooks/${projectId}/${id}`, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks', projectId, 'list'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/webhooks/${projectId}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['webhooks', projectId] });
      toast.success('Webhook deleted');
    },
  });

  const test = useMutation({
    mutationFn: (id: string) =>
      api
        .post<{
          success: boolean;
          data: { statusCode: number; success: boolean; error?: string };
        }>(`/webhooks/${projectId}/${id}/test`)
        .then((r) => r.data.data),
    onSuccess: (d) => {
      if (d.success) toast.success(`Test OK (${d.statusCode})`);
      else toast.error(`Test failed: ${d.error || d.statusCode}`);
      qc.invalidateQueries({ queryKey: ['webhooks', projectId, 'logs'] });
    },
  });

  if (!projectId) return null;

  return (
    <div className="container mx-auto flex max-w-5xl flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Webhook className="h-6 w-6" /> Webhooks
          </h1>
          <p className="text-muted-foreground text-sm">
            Forward platform events to any HTTPS endpoint. Deliveries are signed with an HMAC secret
            shown once on creation.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Add webhook
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Registered endpoints</CardTitle>
          <CardDescription>
            One project can have multiple webhooks; toggle inactive to stop deliveries without
            losing the subscription.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {webhooks.isLoading ? (
            <div className="text-muted-foreground flex items-center gap-2 p-4 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !webhooks.data?.length ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No webhooks registered. Add one to start receiving events.
            </p>
          ) : (
            <div className="divide-y">
              {webhooks.data.map((wh) => (
                <div key={wh.id} className="flex flex-col gap-2 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{wh.name}</span>
                        {wh.isActive ? (
                          <Badge
                            variant="outline"
                            className="border-green-200 bg-green-50 text-green-700"
                          >
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline">Paused</Badge>
                        )}
                      </div>
                      <div className="text-muted-foreground truncate text-xs">{wh.url}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Send test event"
                        disabled={test.isPending}
                        onClick={() => test.mutate(wh.id)}
                      >
                        <Play className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        title={wh.isActive ? 'Pause' : 'Resume'}
                        onClick={() => toggle.mutate({ id: wh.id, isActive: !wh.isActive })}
                      >
                        <Power className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Delete"
                        onClick={async () => {
                          if (
                            await confirm({
                              title: 'Delete webhook?',
                              message: `Delete webhook "${wh.name}"?`,
                              danger: true,
                            })
                          ) {
                            remove.mutate(wh.id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {wh.events.map((e) => (
                      <Badge key={e} variant="secondary" className="text-[10px]">
                        {e}
                      </Badge>
                    ))}
                  </div>
                  {wh.secretHint && (
                    <div className="text-muted-foreground text-xs">
                      Signing secret: <code>{wh.secretHint}</code>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent deliveries</CardTitle>
          <CardDescription>
            Last 50 attempts across all endpoints. Refreshes every 15s.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {logs.isLoading ? (
            <div className="text-muted-foreground flex items-center gap-2 p-4 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !logs.data?.length ? (
            <p className="text-muted-foreground py-6 text-center text-sm">No deliveries yet.</p>
          ) : (
            <div className="divide-y text-sm">
              {logs.data.map((d) => (
                <div key={d._id} className="flex items-center gap-3 py-2">
                  {d.success ? (
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 flex-shrink-0 text-red-600" />
                  )}
                  <span className="font-mono text-xs">{d.eventType}</span>
                  <span className="text-muted-foreground ml-auto text-xs">
                    {d.statusCode || 'ERR'}
                    {d.attemptCount > 1 ? ` · x${d.attemptCount}` : ''}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {new Date(d.deliveredAt).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AddWebhookDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onSubmit={(p) => create.mutate(p)}
        submitting={create.isPending}
      />

      <Dialog open={newSecret != null} onOpenChange={(o) => !o && setNewSecret(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save your signing secret</DialogTitle>
            <DialogDescription>
              We only show this once. Copy it somewhere safe — you'll need it to verify the HMAC
              signature on incoming deliveries.
            </DialogDescription>
          </DialogHeader>
          {newSecret && (
            <div className="bg-muted flex items-center gap-2 rounded-md border p-3">
              <code className="flex-1 break-all text-xs">{newSecret}</code>
              <Button
                size="icon"
                variant="ghost"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(newSecret);
                    toast.success('Secret copied to clipboard');
                  } catch {
                    toast.error('Copy failed — select and copy manually');
                  }
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setNewSecret(null)}>I saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddWebhookDialog({
  open,
  onOpenChange,
  onSubmit,
  submitting,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSubmit: (p: { name: string; url: string; events: string[] }) => void;
  submitting: boolean;
}) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const allEventKeys = useMemo(() => EVENT_CATALOG.flatMap((g) => g.events), []);

  const reset = () => {
    setName('');
    setUrl('');
    setPicked(new Set());
  };

  const handleSubmit = () => {
    if (!name.trim() || !url.trim()) {
      toast.error('Name and URL are required');
      return;
    }
    if (!picked.size) {
      toast.error('Pick at least one event');
      return;
    }
    onSubmit({ name: name.trim(), url: url.trim(), events: Array.from(picked) });
    reset();
  };

  const toggleEvent = (e: string) => {
    setPicked((cur) => {
      const n = new Set(cur);
      if (n.has(e)) n.delete(e);
      else n.add(e);
      return n;
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add webhook</DialogTitle>
          <DialogDescription>
            We'll POST event payloads to this URL. Choose what to subscribe to.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Zapier: new orders"
              maxLength={100}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>URL</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://hooks.zapier.com/…"
              type="url"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label>Events</Label>
            <div className="flex gap-2 text-xs">
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground underline"
                onClick={() => setPicked(new Set(allEventKeys))}
              >
                Select all
              </button>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground underline"
                onClick={() => setPicked(new Set())}
              >
                Clear
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-3 rounded-md border p-3">
            {EVENT_CATALOG.map((grp) => (
              <div key={grp.group} className="flex flex-col gap-1">
                <div className="text-muted-foreground text-xs font-medium">{grp.group}</div>
                <div className="flex flex-wrap gap-1.5">
                  {grp.events.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => toggleEvent(e)}
                      className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
                        picked.has(e)
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'hover:bg-muted'
                      }`}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating…
              </>
            ) : (
              'Create webhook'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
